import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [validateToken, setValidateToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const cleanEmail = email.trim().toLowerCase();
  const cleanOtp = otp.trim();

  const getErrorMessage = (err, fallback) =>
    err?.response?.data?.detail ||
    err?.response?.data?.message ||
    err?.response?.data?.title ||
    fallback;

  const sendCode = async (e) => {
    e.preventDefault();

    if (!cleanEmail) {
      toast.error("Email adresi zorunlu.");
      return;
    }

    try {
      setLoading(true);

      await api.forgotPassword({ email: cleanEmail });

      toast.success("Kod mail adresinize gönderildi.");
      setStep(2);
    } catch (err) {
      toast.error(getErrorMessage(err, "Kod gönderilemedi."));
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();

    if (!cleanOtp) {
      toast.error("Doğrulama kodu zorunlu.");
      return;
    }

    try {
      setLoading(true);

      const res = await api.validateOtp({ otp: cleanOtp });

      const token = res?.data?.validateToken;

      if (!token) {
        toast.error("Doğrulama tokenı alınamadı.");
        return;
      }

      setValidateToken(token);
      toast.success("Kod doğrulandı.");
      setStep(3);
    } catch (err) {
      toast.error(getErrorMessage(err, "Kod doğrulanamadı."));
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      toast.error("Şifre alanları zorunlu.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Şifreler eşleşmiyor.");
      return;
    }

    if (!validateToken) {
      toast.error("Doğrulama tokenı bulunamadı.");
      return;
    }

    try {
      setLoading(true);

      await api.changeForgotPassword({
        newPassword,
        validateToken,
        });

      toast.success("Şifreniz başarıyla güncellendi.");
      navigate("/login");
    } catch (err) {
      toast.error(getErrorMessage(err, "Şifre güncellenemedi."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4">
      <div className="w-full max-w-md rounded-2xl border border-zinc-100 bg-white p-6 shadow-lg">
        <h1 className="text-2xl font-semibold text-zinc-900">
          Şifremi Unuttum
        </h1>

        <p className="mt-2 mb-6 text-sm text-zinc-500">
          {step === 1 && "Mail adresinizi girin, doğrulama kodu gönderelim."}
          {step === 2 && "Mailinize gelen doğrulama kodunu girin."}
          {step === 3 && "Yeni şifrenizi belirleyin."}
        </p>

        {step === 1 && (
          <form onSubmit={sendCode} className="space-y-4">
            <Input
              type="email"
              placeholder="Email adresiniz"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Gönderiliyor..." : "Kod Gönder"}
            </Button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={verifyCode} className="space-y-4">
            <Input
              type="text"
              inputMode="numeric"
              placeholder="Doğrulama kodu"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              required
            />

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Kontrol ediliyor..." : "Kodu Doğrula"}
            </Button>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={resetPassword} className="space-y-4">
            <Input
              type="password"
              placeholder="Yeni şifre"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />

            <Input
              type="password"
              placeholder="Yeni şifre tekrar"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Güncelleniyor..." : "Şifreyi Güncelle"}
            </Button>
          </form>
        )}

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="text-sm font-medium text-orange-500 hover:text-orange-600 hover:underline"
          >
            Giriş ekranına dön
          </Link>
        </div>
      </div>
    </div>
  );
}